import { useMemo, useState } from 'react';
import type { UploadPlanRenameReason, UploadPlanSkipReason } from '@kennisbank/shared';
import { AlertTriangleIcon, FolderOpenIcon, XIcon } from '../icons.js';
import {
  resolveUploads,
  type CollisionChoice,
  type Disposition,
  type PlannedFingerprint,
} from '../../lib/bulk-upload.js';

/**
 * Pre-flight summary for a multi-file / folder drop, shown before any byte
 * is uploaded: what will upload, what is skipped and why, what gets renamed,
 * and a per-file decision for names that already exist with different
 * content (batch shortcuts: Overwrite all / Skip all / Keep both).
 */

export interface PreflightPlannedFile extends PlannedFingerprint {
  originalPath: string;
  renameReasons: UploadPlanRenameReason[];
}

export type PreflightState =
  | { phase: 'checking'; label: string; done: number; total: number }
  | {
      phase: 'ready';
      label: string;
      ringLabel: string;
      planned: PreflightPlannedFile[];
      dispositions: Disposition[];
      skippedEarly: Array<{ path: string; reason: UploadPlanSkipReason }>;
      takenNames: ReadonlySet<string>;
    }
  | { phase: 'error'; label: string; message: string };

const EARLY_SKIP_TEXT: Record<UploadPlanSkipReason, string> = {
  unsupported_type: 'File type not supported',
  too_large: 'Larger than 100 MB',
  empty: 'Empty file',
};

const RENAME_TEXT: Record<UploadPlanRenameReason, string> = {
  sanitised: 'characters the knowledgebase does not accept were replaced',
  subfolder_prefix: 'same name in another folder — folder name added',
  numbered: 'same name as another file — number added',
};

const CHOICE_LABEL: Record<CollisionChoice, string> = {
  overwrite: 'Overwrite',
  skip: 'Skip',
  keep_both: 'Keep both',
};

export function BulkUploadPreflight({
  state,
  onCancel,
  onConfirm,
}: {
  state: PreflightState;
  onCancel: () => void;
  onConfirm: (choices: Map<number, CollisionChoice>) => void;
}): JSX.Element {
  const [choices, setChoices] = useState<Map<number, CollisionChoice>>(new Map());

  const ready = state.phase === 'ready' ? state : null;
  const askIndexes = useMemo(
    () => (ready ? ready.dispositions.flatMap((d, i) => (d.kind === 'ask' ? [i] : [])) : []),
    [ready],
  );
  const resolved = useMemo(
    () =>
      ready ? resolveUploads(ready.planned, ready.dispositions, choices, ready.takenNames) : null,
    [ready, choices],
  );
  const renamed = ready
    ? ready.planned.map((p, i) => ({ p, i })).filter(({ p }) => p.renameReasons.length > 0)
    : [];
  const skippedAfterHash = ready
    ? ready.dispositions.flatMap((d, i) =>
        d.kind === 'skip' ? [{ name: ready.planned[i].originalPath, detail: d.detail }] : [],
      )
    : [];
  const unanswered = askIndexes.filter((i) => !choices.has(i)).length;
  const uploadCount = resolved?.uploads.length ?? null;
  const setAll = (choice: CollisionChoice): void =>
    setChoices(new Map(askIndexes.map((i) => [i, choice])));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-preflight-title"
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-nebula-surface shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-nebula-border-soft px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <FolderOpenIcon size={20} className="text-nebula-secondary" />
            <h3 id="bulk-preflight-title" className="ov-h-section truncate">
              {state.label}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel upload"
            className="rounded-md p-1 text-nebula-fg-4 hover:bg-nebula-gray-5 hover:text-nebula-fg-1"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="thin-scroll flex-1 space-y-5 overflow-y-auto px-6 py-4">
          {state.phase === 'checking' && (
            <div>
              <p className="ov-body">
                Checking files… {state.done.toLocaleString('en-GB')} /{' '}
                {state.total.toLocaleString('en-GB')}
              </p>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-nebula-tint">
                <div
                  className="h-full bg-nebula-secondary transition-all"
                  style={{ width: `${state.total ? (state.done / state.total) * 100 : 0}%` }}
                />
              </div>
              <p className="ov-meta mt-2">
                Fingerprinting each file so identical documents are not uploaded twice.
              </p>
            </div>
          )}

          {state.phase === 'error' && (
            <p className="rounded-md border border-nebula-error/20 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
              {state.message}
            </p>
          )}

          {ready && (
            <>
              <div className="rounded-md bg-nebula-tint px-4 py-3">
                <p className="ov-body-strong">
                  {uploadCount === null
                    ? `${ready.planned.length - skippedAfterHash.length - unanswered} files ready, ${unanswered} need a decision`
                    : `${uploadCount.toLocaleString('en-GB')} file${uploadCount === 1 ? '' : 's'} will be uploaded to ${ready.ringLabel}`}
                </p>
                <p className="ov-meta mt-1">
                  {ready.skippedEarly.length + skippedAfterHash.length} skipped · {renamed.length}{' '}
                  renamed · {askIndexes.length} already exist with different content
                </p>
              </div>

              {askIndexes.length > 0 && (
                <section>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="ov-h-card flex items-center gap-1.5">
                      <AlertTriangleIcon size={14} className="text-nebula-warning" />
                      Name already in use ({askIndexes.length})
                    </h4>
                    <div className="flex gap-1">
                      {(['overwrite', 'skip', 'keep_both'] as const).map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setAll(c)}
                          className="rounded-md border border-nebula-border px-2 py-1 text-xs text-nebula-fg-2 hover:bg-nebula-tint"
                        >
                          {CHOICE_LABEL[c]} all
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="ov-meta mb-2">
                    These names exist in the knowledgebase with different content. Keep both uploads
                    the new file under a numbered name.
                  </p>
                  <ul className="divide-y divide-nebula-border-soft rounded-md border border-nebula-border-soft">
                    {askIndexes.map((i) => (
                      <li key={i} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="min-w-0 truncate text-sm text-nebula-fg-1">
                          {ready.planned[i].finalName}
                        </span>
                        <div
                          role="radiogroup"
                          aria-label={`Action for ${ready.planned[i].finalName}`}
                          className="inline-flex flex-shrink-0 overflow-hidden rounded-md border border-nebula-border text-xs"
                        >
                          {(['overwrite', 'skip', 'keep_both'] as const).map((c, ci) => {
                            const active = choices.get(i) === c;
                            return (
                              <button
                                key={c}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => setChoices((cur) => new Map(cur).set(i, c))}
                                className={[
                                  'px-2 py-1',
                                  ci > 0 ? 'border-l border-nebula-border' : '',
                                  active
                                    ? 'bg-nebula-primary text-white'
                                    : 'bg-white text-nebula-fg-2 hover:bg-nebula-tint',
                                ].join(' ')}
                              >
                                {CHOICE_LABEL[c]}
                              </button>
                            );
                          })}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {renamed.length > 0 && (
                <section>
                  <h4 className="ov-h-card mb-2">Renamed ({renamed.length})</h4>
                  <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md bg-nebula-gray-5 p-2 text-xs">
                    {renamed.map(({ p, i }) => (
                      <li key={i} className="text-nebula-fg-2">
                        <span className="text-nebula-fg-4">{p.originalPath}</span> →{' '}
                        <span className="font-medium">{p.finalName}</span>{' '}
                        <span className="text-nebula-fg-4">
                          ({p.renameReasons.map((r) => RENAME_TEXT[r]).join('; ')})
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {ready.skippedEarly.length + skippedAfterHash.length > 0 && (
                <section>
                  <h4 className="ov-h-card mb-2">
                    Skipped ({ready.skippedEarly.length + skippedAfterHash.length})
                  </h4>
                  <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md bg-nebula-gray-5 p-2 text-xs">
                    {skippedAfterHash.map((s, i) => (
                      <li key={`h${i}`} className="text-nebula-fg-2">
                        {s.name} — <span className="text-nebula-fg-4">{s.detail}</span>
                      </li>
                    ))}
                    {ready.skippedEarly.map((s, i) => (
                      <li key={`e${i}`} className="text-nebula-fg-2">
                        {s.path} —{' '}
                        <span className="text-nebula-fg-4">{EARLY_SKIP_TEXT[s.reason]}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-nebula-border-soft px-6 py-3">
          <p className="ov-meta">
            {ready
              ? 'Keep this tab open while files upload. Once uploaded, indexing continues on the server.'
              : ''}
          </p>
          <div className="flex flex-shrink-0 gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-nebula-border px-3 py-1.5 text-sm text-nebula-fg-2 hover:bg-nebula-tint"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!resolved || resolved.uploads.length === 0}
              onClick={() => onConfirm(choices)}
              className="rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-secondary disabled:cursor-not-allowed disabled:opacity-40"
            >
              {uploadCount === null
                ? `Decide ${unanswered} more`
                : `Upload ${uploadCount.toLocaleString('en-GB')} file${uploadCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
